<script setup>
import { ref } from 'vue'
import { Link, router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import VendorPortalLayout from '@/Layouts/VendorPortalLayout.vue'
import BlackoutModal from '@/Components/VendorPortal/BlackoutModal.vue'

defineOptions({ layout: VendorPortalLayout })

const { t } = useI18n()
const props = defineProps({ tour: { type: Object, required: true } })

const activeIdx = ref(0)
const photos = props.tour.photo_urls?.length ? props.tour.photo_urls : []
const showBlackoutModal = ref(false)

function clearBlackout(b) {
  if (!b.owned_by_me) return
  if (!confirm(t('vendor_portal.tour.clear_blackout_confirm'))) return
  router.delete(`/tours/${props.tour.id}/blackouts/${b.id}`)
}
</script>

<template>
  <Link
    href="/"
    class="back"
  >
    ← {{ t('vendor_portal.tour.back') }}
  </Link>
  <div class="title-row">
    <h1 class="title">
      {{ tour.title }}
    </h1>
    <Link
      :href="`/tours/${tour.id}/edit`"
      class="edit-btn"
    >
      {{ t('vendor_portal.tour.edit') }}
    </Link>
  </div>

  <div
    v-if="tour.approval_state !== 'approved'"
    class="approval-pill"
    :class="`approval-pill--${tour.approval_state}`"
  >
    {{ t(`vendor_portal.tour.approval.${tour.approval_state}`) }}
    <span
      v-if="tour.rejection_reason"
      class="approval-pill__reason"
    >— {{ tour.rejection_reason }}</span>
  </div>

  <div class="layout">
    <div class="gallery">
      <div class="gallery__main">
        <img
          v-if="photos[activeIdx]"
          :src="photos[activeIdx]"
          :alt="tour.title"
        >
        <div
          v-else
          class="gallery__placeholder"
        >
          {{ tour.title?.[0] }}
        </div>
      </div>
      <ul
        v-if="photos.length > 1"
        class="thumbs"
      >
        <li
          v-for="(p, i) in photos"
          :key="p"
        >
          <button
            :class="['thumb', i === activeIdx && 'is-active']"
            @click="activeIdx = i"
          >
            <img
              :src="p"
              alt=""
            >
          </button>
        </li>
      </ul>
    </div>

    <div class="info">
      <section class="card">
        <h2>{{ t('vendor_portal.tour.sections.details') }}</h2>
        <dl class="detail-list">
          <div class="detail-row">
            <dt>{{ t('vendor_portal.tour_form.fields.category') }}</dt><dd>{{ t(`vendor_portal.tour.categories.${tour.category}`) }}</dd>
          </div>
          <div class="detail-row">
            <dt>{{ t('vendor_portal.tour_form.fields.duration_minutes') }}</dt><dd>{{ tour.duration_minutes }} min</dd>
          </div>
          <div class="detail-row">
            <dt>{{ t('vendor_portal.tour_form.fields.price_adult') }}</dt><dd class="fw-value">
              {{ tour.price_adult }}
            </dd>
          </div>
          <div
            v-if="tour.price_child"
            class="detail-row"
          >
            <dt>{{ t('vendor_portal.tour_form.fields.price_child') }}</dt><dd class="fw-value">
              {{ tour.price_child }}
            </dd>
          </div>
          <div
            v-if="tour.max_group_size"
            class="detail-row"
          >
            <dt>{{ t('vendor_portal.tour_form.fields.max_group_size') }}</dt><dd>{{ tour.max_group_size }}</dd>
          </div>
          <div
            v-if="tour.languages_offered"
            class="detail-row"
          >
            <dt>{{ t('vendor_portal.tour_form.fields.languages_offered') }}</dt><dd>{{ tour.languages_offered }}</dd>
          </div>
          <div
            v-if="tour.meeting_point_address"
            class="detail-row"
          >
            <dt>{{ t('vendor_portal.tour_form.fields.meeting_point') }}</dt><dd>{{ tour.meeting_point_address }}</dd>
          </div>
          <div
            v-if="tour.meeting_point_description"
            class="detail-row"
          >
            <dt>{{ t('vendor_portal.tour_form.fields.meeting_point_description') }}</dt>
            <dd class="prose-inline">{{ tour.meeting_point_description }}</dd>
          </div>
        </dl>
      </section>

      <section
        v-if="tour.description"
        class="card"
      >
        <h2>{{ t('vendor_portal.tour.sections.description') }}</h2>
        <p class="prose">
          {{ tour.description }}
        </p>
      </section>

      <section
        v-if="tour.inclusions"
        class="card"
      >
        <h2>{{ t('vendor_portal.tour.sections.inclusions') }}</h2>
        <p class="prose">
          {{ tour.inclusions }}
        </p>
      </section>

      <section class="card">
        <div class="card__header">
          <h2>{{ t('vendor_portal.tour.sections.blackouts') }}</h2>
          <button
            class="btn"
            @click="showBlackoutModal = true"
          >
            {{ t('vendor_portal.tour.add_blackout') }}
          </button>
        </div>
        <ul class="blocks">
          <li
            v-for="b in tour.blackouts"
            :key="b.id"
            class="block-row"
          >
            {{ b.start_on }} → {{ b.end_on }}
            <span
              v-if="b.reason"
              class="muted"
            >· {{ b.reason }}</span>
            <span
              v-if="b.cleared_at"
              class="muted"
            >· {{ t('vendor_portal.tour.cleared') }}</span>
            <button
              v-if="b.owned_by_me && !b.cleared_at"
              class="link"
              @click="clearBlackout(b)"
            >
              {{ t('vendor_portal.tour.clear') }}
            </button>
          </li>
          <li
            v-if="tour.blackouts.length === 0"
            class="empty"
          >
            {{ t('vendor_portal.tour.no_blackouts') }}
          </li>
        </ul>
        <BlackoutModal
          v-if="showBlackoutModal"
          :tour="tour"
          @close="showBlackoutModal = false"
        />
      </section>
    </div>
  </div>
</template>

<style scoped>
.back  { color: var(--muted); text-decoration: none; font-size: 0.875rem; }
.title-row { display: flex; align-items: center; gap: 1rem; margin: 0.5rem 0 1.25rem; }
.title { font-family: var(--font-display, var(--display)); font-size: 1.5rem; color: var(--ink); margin: 0; flex: 1; }
.edit-btn { padding: 0.4rem 0.85rem; border: 1px solid var(--line); border-radius: var(--radius-button, 8px); color: var(--ink); text-decoration: none; font-size: 0.875rem; font-weight: 500; }
.edit-btn:hover { border-color: var(--accent); color: var(--accent); }
.approval-pill { display: inline-block; padding: 0.4rem 0.75rem; margin: 0 0 1rem; border-radius: 999px; font-size: 0.8125rem; font-weight: 500; }
.approval-pill--pending  { background: color-mix(in oklch, var(--warn) 16%, var(--surface)); color: var(--warn); }
.approval-pill--rejected { background: color-mix(in oklch, var(--danger) 14%, var(--surface)); color: var(--danger); }
.approval-pill__reason { font-weight: 400; }
.layout { display: grid; grid-template-columns: 1fr; gap: 1.5rem; }
@media (min-width: 880px) { .layout { grid-template-columns: 1.1fr 1fr; gap: 2rem; } }
.gallery__main { aspect-ratio: 4 / 3; background: var(--surfaceAlt); border-radius: 0.75rem; overflow: hidden; display: grid; place-items: center; }
.gallery__main img { width: 100%; height: 100%; object-fit: cover; }
.gallery__placeholder { font-size: 4rem; font-weight: 700; color: var(--muted); }
.thumbs { list-style: none; padding: 0; margin: 0.75rem 0 0; display: flex; gap: 0.5rem; overflow-x: auto; }
.thumb { width: 4.5rem; height: 3.5rem; padding: 0; border: 2px solid transparent; border-radius: 0.5rem; overflow: hidden; cursor: pointer; background: var(--surfaceAlt); }
.thumb.is-active { border-color: var(--accent); }
.thumb img { width: 100%; height: 100%; object-fit: cover; }
.info { display: grid; gap: 1rem; }
.card { background: var(--surface); border: 1px solid var(--line); border-radius: 0.75rem; padding: 1rem 1.25rem; }
.card__header { display: flex; justify-content: space-between; align-items: center; }
.card h2 { font-size: 0.875rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); margin: 0 0 0.5rem; }
.detail-list { display: grid; gap: 0.5rem; margin: 0; }
.detail-row { display: grid; grid-template-columns: 10rem 1fr; gap: 0.5rem; align-items: baseline; }
.detail-row dt { font-size: 0.8125rem; color: var(--muted); }
.detail-row dd { font-size: 0.9375rem; color: var(--ink); margin: 0; }
.prose { color: var(--inkSoft); line-height: 1.6; white-space: pre-wrap; margin: 0; }
.prose-inline { white-space: pre-wrap; }
.btn { padding: 0.35rem 0.7rem; border: 1px solid var(--line); background: var(--surfaceAlt); color: var(--ink); border-radius: var(--radius-button, 8px); cursor: pointer; font-size: 0.8125rem; }
.blocks { list-style: none; padding: 0; margin: 0; }
.block-row { padding: 0.4rem 0; border-bottom: 1px solid var(--line); font-size: 0.875rem; }
.block-row:last-child { border-bottom: 0; }
.muted { color: var(--muted); font-size: 0.8125rem; margin-left: 0.4rem; }
.link { background: none; border: 0; color: var(--accent); cursor: pointer; font-size: 0.8125rem; margin-left: 0.5rem; }
.empty { color: var(--muted); padding: 0.5rem 0; }
</style>
